import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Disclaimer from "../../components/Disclaimer/Disclaimer";
import Layout from "../../components/Layout/Layout";
import { FESTS, KINDS, REGIONS } from "../../data/fests";
import { KIND_LABELS, kindLabel, matchesFilters, overnightLabel, TAG_LABELS } from "../../lib/access";
import { formatRange, nextSeason } from "../../lib/season";
import type { Fest, FestKind } from "../../types";

const Fests = () => {
  const [region, setRegion] = useState<Fest["region"] | "">("");
  const [kind, setKind] = useState<FestKind | "">("");
  const [camp, setCamp] = useState(false);
  const [rvFriendly, setRvFriendly] = useState(false);

  const filtered = useMemo(
    () => FESTS.filter((fest) => matchesFilters(fest, { region, kind, camp, rvFriendly })),
    [region, kind, camp, rvFriendly],
  );

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 py-12 w-full">
        <p className="text-sun font-semibold uppercase tracking-wide text-sm">
          Curated starting points
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-alt-blk mt-3">
          Lots you can sleep on
        </h1>
        <p className="text-xl text-alt-blk/80 mt-4 max-w-3xl leading-relaxed">
          Outdoor music gatherings from death metal to pagan rites — US fields
          and European open airs. Dates and lot notes first. Confirm on the
          official page before you roll.
        </p>
        <div className="mt-4">
          <Disclaimer />
        </div>

        <div className="flex flex-wrap gap-2 mt-8">
          {(["", ...REGIONS] as const).map((value) => (
            <button
              key={value || "all"}
              className={`text-sm px-4 py-1.5 rounded-full border ${
                region === value
                  ? "bg-mars border-mars text-white"
                  : "border-alt-blk/20 hover:border-mars"
              }`}
              onClick={() => setRegion(value)}
            >
              {value || "All regions"}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(["", ...KINDS] as const).map((value) => (
            <button
              key={value || "all-kinds"}
              className={`text-sm px-4 py-1.5 rounded-full border ${
                kind === value
                  ? "bg-mars border-mars text-white"
                  : "border-alt-blk/20 hover:border-mars"
              }`}
              onClick={() => setKind(value)}
            >
              {value ? KIND_LABELS[value] : "All kinds"}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Chip label="On the lot" active={camp} onClick={() => setCamp((v) => !v)} />
          <Chip
            label="RV-friendly"
            active={rvFriendly}
            onClick={() => setRvFriendly((v) => !v)}
          />
        </div>

        <p className="text-sm text-alt-blk/60 mt-6">
          {filtered.length} fest{filtered.length === 1 ? "" : "s"}
        </p>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {filtered.map((fest) => {
            const season = nextSeason(fest);
            return (
              <article
                key={fest.id}
                className="rounded-2xl bg-white border border-alt-blk/10 p-6 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-sun">
                      {fest.region} · {fest.city}, {fest.state}
                    </p>
                    <h2 className="text-2xl font-bold mt-1">{fest.name}</h2>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-turquoise">
                      {kindLabel(fest)}
                    </div>
                    <div className="text-xs text-alt-blk/50">
                      {overnightLabel(fest.overnight)}
                    </div>
                  </div>
                </div>
                {season && (
                  <p className="text-sm text-alt-blk/60 mt-2">
                    {formatRange(season.start, season.end)} ·{" "}
                    {season.cadence === "weekends" ? "weekends" : season.cadence}
                  </p>
                )}
                <p className="mt-3 text-alt-blk/80 leading-relaxed">{fest.whyGo}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {fest.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-alt-blk/15 text-alt-blk/70"
                    >
                      {TAG_LABELS[tag]}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-alt-blk/70 mt-4">{fest.campingNotes}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to={`/fests/${fest.id}`}
                    className="inline-block bg-mars hover:bg-sunset text-white px-5 py-2.5 rounded-2xl font-semibold text-sm"
                  >
                    Trip notes
                  </Link>
                  <Link
                    to={`/?fest=${fest.id}`}
                    className="inline-block border border-alt-blk px-5 py-2.5 rounded-2xl font-semibold text-sm hover:bg-alt-blk hover:text-white transition-colors"
                  >
                    Show on map
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`text-sm px-4 py-1.5 rounded-full border ${
        active ? "bg-mars border-mars text-white" : "border-alt-blk/20 hover:border-mars"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default Fests;
